import React, { useState, useEffect } from 'react'
import {
  Alert,
  Spinner,
  Button,
  OverlayTrigger,
  Tooltip
} from 'react-bootstrap'
import {
  FormControl,
  Select,
  MenuItem,
  Typography,
  IconButton,
} from '@mui/material'
import '../styles/PrintCollaterals.css'
import CustomDataGrid from '../components/CustomDataGrid'
import {
  generateGridColumns,
  pdfGridColumns,
  pdfRequestRowMapping,
  generateGridRows,
  formatUtcToLocal,
  disableLink,
} from '../utils/PageUtils'
import {
  fetchExistingCampaigns,
  getCustomizedTemplatesCreatedByUser,
  requestForPDFGeneration,
  fetchPdfGenerationRequestsForCustomer,
} from '../utils/api'
import PageHelmet from '../components/PageHelmet'
import RefreshIcon from '@mui/icons-material/Refresh'
import PropTypes from 'prop-types'

function PrintCollaterals({ userRole, bearerToken }) {
  const [error, setError] = useState(null)
  const [responseError, setResponseError] = useState(null)
  const [templateError, setTemplateError] = useState(null)

  const [campaignsData, setCampaignsData] = useState([])
  const [existingTemplates, setExistingTemplates] = useState([])
  const [selectedCampaign, setSelectedCampaign] = useState({})
  const [selectedTemplate, setSelectedTemplate] = useState(null)
  const [isInitialLoading, setIsInitialLoading] = useState(true)
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState(null)

  const [pdfRequests, setPdfRequests] = useState([])
  const [refreshRequests, setRefreshRequests] = useState(true)

  const dropdownStyles = {
    borderRadius: '8px',
    border: '1px solid #ccc',
    fontSize: '14px',
    backgroundColor: '#f9f9f9',
    '&:hover': {
      backgroundColor: '#f1f1f1',
    },
    width: '100%',
  }

  const menuItemStyles = {
    padding: '10px 16px',
    fontSize: '14px',
    '&:hover': {
      backgroundColor: '#e0f7fa',
      color: '#00796b',
    },
  }

  useEffect(() => {
    const getCampaigns = async () => {
      setResponseError(null)
      setTemplateError(null)
      setIsInitialLoading(true)
      if (!bearerToken) {
        console.warn('No access token available')
        return
      }
      const result = await fetchExistingCampaigns(bearerToken)
      console.log('result', result)
      console.log('Existing campaigns', result.data)

      if (!result.success) {
        setResponseError(result.error)
        setCampaignsData([])
        setIsInitialLoading(false)
        return
      }

      const campaignsWithContacts = await result.data.filter(
        (campaign) => campaign.total > 0,
      )
      console.log('Campaigns with contacts', campaignsWithContacts)
      setCampaignsData(campaignsWithContacts)

      const response =
        await getCustomizedTemplatesCreatedByUser(bearerToken)
      console.log('existing templates', existingTemplates)
      if (response.status == 200) {
        let templates = response?.data?.records;
        setExistingTemplates(templates)
        setIsInitialLoading(false)
        return
      }
      else if (response.status == 404) {
        setTemplateError(
          'Please create a customized template first and then come back here',
        )
        console.log(`template error`, templateError)
        setExistingTemplates([])
        setIsInitialLoading(false)
        return
      }
      else {
        setTemplateError(response.message)
        setExistingTemplates([])
        setIsInitialLoading(false)
        return;
      }
    }
    getCampaigns()
  }, [])

  useEffect(() => {
    const fetchExistingRequests = async () => {
      setError(null)
      setLoading(true)
      try {
        const existingRequests =
          await fetchPdfGenerationRequestsForCustomer(bearerToken)

        console.log('existing requests', existingRequests)

        if (!existingRequests || existingRequests.count === 0) {
          setPdfRequests([])
          return
        }

        const updateDataset = existingRequests.records.map((record) => ({
          ...record,
          request_timestamp: formatUtcToLocal(
            record.request_timestamp,
          ),
          conditional_url: disableLink(record),
        }))

        console.log('updated dataset', updateDataset)

        updateDataset.sort((a, b) => {
          b.request_timestamp.localeCompare(a.request_timestamp)
        })
        setPdfRequests(updateDataset)
      } catch (error) {
        setError(error.message)
      } finally {
        setLoading(false)
      }
    }
    if (refreshRequests && existingTemplates.length > 0) {
      fetchExistingRequests()
      setRefreshRequests(false)
    }
  }, [refreshRequests, existingTemplates])

  const sendRequestForPdfGeneration = async (campaign, template) => {
    setError(null)
    setMessage(null)

    if (!campaign || !template) {
      setError(
        'Please select both, campaign and customized template to generate print collaterals against them',
      )
      return
    }
    setLoading(true)
    const result = await requestForPDFGeneration(
      campaign,
      template,
      bearerToken,
    )

    if (result.success) {
      setMessage(result.message)
      setRefreshRequests(true)
    } else {
      setError(result.error)
    }
    setLoading(false)
  }

  const pdfRequestsRow = generateGridRows(pdfRequests, pdfRequestRowMapping)

  const pdfRequestColumns = generateGridColumns(pdfGridColumns, 'campaign')

  if (isInitialLoading) {
    return (
      <div className="loading-container">
        <Spinner animation="border" role="status" />
        <p>Please wait...</p>
      </div>
    )
  }

  function formatMessage(msg) {
    return msg.split('\n').map((line, ind) => (
      <span key={ind}>
        {line}
        <br />
      </span>
    ));
  }
  return userRole === 'customer' ? (
    <div className="print-content-wrapper">
      <PageHelmet
        title="Generate Print Collaterals"
        description="Generate pdfs with TraQR."
        url={`${process.env.REACT_APP_COGNITO_REDIRECT_URL}/print_content`}
      />
      <div
        className={`print-container  ${responseError || templateError ? 'no-background' : ''
          }`}
      >
        <h1 className="main-heading">TraQR - Generate File to Print</h1>

        {responseError || templateError ? (
          <div className="create-print-msg">
            {responseError && <Alert variant="danger">{responseError}</Alert>}
            {templateError && <Alert variant="danger">{templateError}</Alert>}
          </div>
        ) : (
          <div className="print-content-container">
            <FormControl
              className="print-content-form"
              style={{ width: 'auto', marginTop: '20px', marginBottom: '2%' }}
            >
              <Typography
                variant="body2"
                style={{
                  marginBottom: '10px',
                  fontWeight: 'bold',
                  fontSize: '14px',
                  color: campaignsData.length === 0 ? 'gray' : 'black',
                }}
                className="custom-label"
              >
                Choose a campaign{''}
                <span style={{ color: 'red' }}> *</span>
              </Typography>
              <div className="campaign-select-container">
                <Select
                  defaultValue="Select a campaign" // Default to first option
                  style={dropdownStyles}
                  disabled={campaignsData.length === 0}
                  onChange={(e) =>
                    setSelectedCampaign(
                      campaignsData?.find(
                        (c) => c.campaign_name === e.target.value,
                      ),
                    )
                  }
                  renderValue={(selected) => {
                    const campaign = campaignsData.find(
                      (c) => c.campaign_name === selected,
                    )
                    if (!campaign) return selected

                    return (
                      <div className="hide-scrollbar">
                        {campaign.campaign_name} ({campaign.destination_url}) -{' '}
                        {campaign.total} contacts
                      </div>
                    )
                  }}
                >
                  <MenuItem value="select a campaign">
                    Select a campaign
                  </MenuItem>
                  {campaignsData?.map((campaign, index) => (
                    <MenuItem
                      key={index}
                      value={campaign.campaign_name}
                      sx={menuItemStyles}
                    >
                      {campaign.campaign_name} ({campaign.destination_url}) - {" "}
                      {`${campaign.total} contacts`}
                    </MenuItem>
                  ))}
                </Select>
              </div>
            </FormControl>
            <FormControl style={{ width: 'auto' }}>
              <Typography
                variant="body2"
                style={{
                  marginBottom: '10px',
                  fontWeight: 'bold',
                  fontSize: '14px',
                }}
                className="custom-label"
              >
                Choose your proofed template {''}
                <span style={{ color: 'red' }}> *</span>
              </Typography>
              <div className="campaign-select-container">
                <Select
                  defaultValue="Select a template"
                  style={dropdownStyles}
                  onChange={(e) =>
                    setSelectedTemplate(
                      existingTemplates.find(
                        (t) => t.template_name === e.target.value,
                      ),
                    )
                  }
                  renderValue={(selected) => {
                    const template = existingTemplates.find(
                      (t) => t.template_name === selected,
                    )
                    if (!template) return selected

                    return (
                      <div className="hide-scrollbar">
                        [{template.choice}] - {template.template_name}
                      </div>
                    )
                  }}
                >
                  <MenuItem value="template">Select a template</MenuItem>
                  {existingTemplates?.map((template, index) => (
                    <MenuItem
                      key={index}
                      value={template.template_name}
                      sx={menuItemStyles}
                    >
                      [{template.choice}] - {template.template_name}
                    </MenuItem>
                  ))}
                </Select>
              </div>
              <div style={{ textAlign: 'center' }}>
                <Button
                  variant="dark"
                  className="responsive-button"
                  disabled={
                    !selectedCampaign?.campaign_name ||
                    !selectedTemplate?.template_name
                  }
                  onClick={() => {
                    sendRequestForPdfGeneration(
                      selectedCampaign.campaign_name,
                      selectedTemplate.template_name,
                    )
                  }}
                >
                  Generate
                </Button>
              </div>
            </FormControl>
          </div>
        )}
      </div>
      {!responseError && !templateError && (
        <>
          {error && (
            <div className="create-print-msgs">
              <Alert variant="danger">{error}</Alert>
            </div>
          )}

          {message && (
            <div className="create-print-msgs" data-testid="print-info-msg">
              <Alert variant="success">{formatMessage(message)}</Alert>
            </div>
          )}

          {loading ? (
            <div className="loading-container">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Loading...</span>
              </Spinner>
            </div>
          ) : (
            <div className="existing-requests-container">
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  margin: '10px 0',
                  width: '100%',
                }}
              >
                <h2 className="requests-heading">Print Collateral Requests</h2>
                <OverlayTrigger
                  placement="top"
                  overlay={
                    <Tooltip id="refresh-tooltip">
                      <span style={{ fontSize: '10px' }}>Refresh Grid</span>
                    </Tooltip>
                  }
                >
                  <IconButton
                    onClick={() => setRefreshRequests(true)}
                    aria-label="Refresh grid"
                    className="refresh-icon-button"
                    sx={{
                      width: '36px',
                      height: '36px',
                      backgroundColor: '#fff',
                      borderRadius: '50%',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                      color: '#2f539b',
                      padding: 0,
                    }}
                  >
                    <RefreshIcon fontSize="small" />
                  </IconButton>
                </OverlayTrigger>
              </div>

              <div className="requests-grid">
                <CustomDataGrid
                  rows={pdfRequestsRow}
                  columns={pdfRequestColumns}
                  totalRows={pdfRequests.length}
                />
              </div>
            </div>
          )}
        </>
      )}
    </div>
  ) : (
    <div className="user-container">
      <Alert variant="danger">You do not have access to this page.</Alert>
    </div>
  )
}

PrintCollaterals.propTypes = {
  userRole: PropTypes.string.isRequired,
  bearerToken: PropTypes.string.isRequired,
}

export default PrintCollaterals
